import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  COMPETITION_STORAGE_KEY,
  DEFAULT_COMPETITION_SLUG
} from '../constants/competition';

const CompetitionContext = createContext(null);

export const useCompetition = () => {
  const ctx = useContext(CompetitionContext);
  if (!ctx) {
    throw new Error('useCompetition must be used within CompetitionProvider');
  }
  return ctx;
};

/** Safe for routes without provider (should not happen if app is wired correctly). */
export const useCompetitionOptional = () => useContext(CompetitionContext);

function parseSlugFromPath(pathname) {
  const m = pathname.match(/^\/c\/([^/]+)/);
  return m ? m[1].toLowerCase() : null;
}

function readStoredSlug() {
  try {
    const s = localStorage.getItem(COMPETITION_STORAGE_KEY);
    return s ? s.toLowerCase() : null;
  } catch {
    return null;
  }
}

export function CompetitionProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Persisted slug when URL has no /c/:slug (admin, settings, etc.) */
  const [storedSlug, setStoredSlug] = useState(readStoredSlug);

  const urlSlug = parseSlugFromPath(location.pathname);

  const refreshCompetitions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/competitions');
      setCompetitions(Array.isArray(data) ? data : []);
    } catch {
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/competitions');
        if (!cancelled) setCompetitions(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCompetitions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const competitionSlug = useMemo(() => {
    if (urlSlug) return urlSlug;
    if (storedSlug) return storedSlug;
    const def = competitions.find((c) => c.isDefault);
    if (def?.slug) return def.slug.toLowerCase();
    if (competitions[0]?.slug) return competitions[0].slug.toLowerCase();
    return DEFAULT_COMPETITION_SLUG;
  }, [urlSlug, storedSlug, competitions]);

  const currentCompetition = useMemo(() => {
    return (
      competitions.find((c) => c.slug?.toLowerCase() === competitionSlug) || null
    );
  }, [competitions, competitionSlug]);

  useEffect(() => {
    if (urlSlug) {
      setStoredSlug(urlSlug);
      try {
        localStorage.setItem(COMPETITION_STORAGE_KEY, urlSlug);
      } catch {
        /* ignore */
      }
    }
  }, [urlSlug]);

  useEffect(() => {
    if (loading || competitions.length === 0 || !urlSlug) return;
    const exists = competitions.some((c) => c.slug?.toLowerCase() === urlSlug);
    if (!exists) {
      navigate(`/c/${DEFAULT_COMPETITION_SLUG}/dashboard`, { replace: true });
    }
  }, [loading, competitions, urlSlug, navigate]);

  const setCompetition = useCallback(
    (slug) => {
      const next = String(slug || DEFAULT_COMPETITION_SLUG).toLowerCase();
      setStoredSlug(next);
      try {
        localStorage.setItem(COMPETITION_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }

      const m = location.pathname.match(/^\/c\/[^/]+(\/.*)?$/);
      if (m) {
        const rest = m[1] && m[1].length > 1 ? m[1] : '/dashboard';
        navigate(`/c/${next}${rest}`);
      }
      // On /admin/*, /settings, etc. — storedSlug updates context for admin pages
    },
    [location.pathname, navigate]
  );

  const basePath = `/c/${competitionSlug}`;

  const value = useMemo(
    () => ({
      competitions,
      loading,
      competitionSlug,
      currentCompetition,
      setCompetition,
      basePath,
      refreshCompetitions,
      /** True when URL is under /c/:slug */
      isCompetitionRoute: Boolean(urlSlug)
    }),
    [
      competitions,
      loading,
      competitionSlug,
      currentCompetition,
      setCompetition,
      basePath,
      refreshCompetitions,
      urlSlug
    ]
  );

  return (
    <CompetitionContext.Provider value={value}>
      {children}
    </CompetitionContext.Provider>
  );
}
